<template>
  <div :class="['hunt-bio-card', customClass]" @click.stop="toggleExpanded($event)">
    <!-- Main info always visible -->
    <h5>{{ hunt.huntId }}</h5>
    <p>
      <strong>Hunt Unit:</strong> {{ hunt.huntId }}
    </p>

    <!-- Save Hunt Icon -->
    <span class="save-icon" @click.prevent.stop="toggleSave">
      <i :class="[isHuntSaved ? 'mdi mdi-bookmark' : 'mdi mdi-bookmark-outline']"></i>
    </span>

    <!-- Collapse/Expand Icon -->
    <span class="toggle-icon">
      <i :class="[isExpanded ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down']"></i>
    </span>

    <!-- Use a transition for a smoother show/hide -->
    <transition name="fade">
      <div v-if="isExpanded" class="extended-info">
        <!-- Hunt Details -->
        <div class="hunt-details">
          <!-- Source Header -->
          <div class="source-header">
            <a href="https://dwrapps.utah.gov/huntboundary/" target="_blank" rel="noopener noreferrer">
              (Sourced from the public Utah Hunt Planner)
            </a>
          </div>

          <!-- General Information -->
          <div class="section" v-if="hunt.GENERAL_INFORMATION">
            <h6>General Information</h6>
            <p v-html="hunt.GENERAL_INFORMATION"></p>
          </div>

          <!-- Biologist Notes -->
          <div class="section" v-if="hunt.BIOLOGIST_NOTES">
            <h6>Biologist Notes</h6>
            <p v-html="hunt.BIOLOGIST_NOTES"></p>
          </div>

          <!-- Weather Considerations -->
          <div class="section" v-if="hunt.WEATHER_CONS">
            <h6>Weather Considerations</h6>
            <p v-html="hunt.WEATHER_CONS"></p>
          </div>

          <!-- Safety Considerations -->
          <div class="section" v-if="hunt.SAFETY_CONS">
            <h6>Safety Considerations</h6>
            <p v-html="hunt.SAFETY_CONS"></p>
          </div>

          <!-- Management Objectives -->
          <div class="section" v-if="hunt.MANAGEMENT_OBJ">
            <h6>Management Objectives</h6>
            <p v-html="formattedManagementObj"></p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import "@mdi/font/css/materialdesignicons.css";
import { mapState } from 'vuex';

export default {
  name: "HuntBioCard",
  props: {
    hunt: {
      type: Object,
      required: true,
    },
    customClass: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      isExpanded: false,
    };
  },
  computed: {
    ...mapState({
      savedHunts: state => state.savedHunts,
      isAuthenticated: state => state.isAuthenticated
    }),
    isHuntSaved() {
      const huntId = this.hunt.huntApplyId;
      const isSaved = this.savedHunts.includes(huntId);
      return isSaved;
    },
    successRate() {
      if (!this.hunt?.MANAGEMENT_OBJ) return null;
      const parts = this.hunt.MANAGEMENT_OBJ.split('~');
      return parts[0] ? parseFloat(parts[0]).toFixed(1) : null;
    },
    formattedManagementObj() {
      if (!this.hunt?.MANAGEMENT_OBJ) return '';
      // Remove the success rate from the beginning and split by ~
      const parts = this.hunt.MANAGEMENT_OBJ.split('~');
      // The last part often contains a link, which we want to preserve as HTML
      const lastPart = parts[parts.length - 1];
      // Join the remaining parts with commas, excluding empty strings
      const middleParts = parts.slice(1, -1)
        .filter(part => part.trim())
        .join(', ');
      return middleParts + (lastPart ? '<br><br>' + lastPart : '');
    }
  },
  methods: {
    async toggleSave(event) {
      event.stopPropagation();
      event.preventDefault();
      if (!this.isAuthenticated) {
        this.$emit('error', 'Please sign in to save hunts');
        return;
      }
      
      const huntId = this.hunt.huntApplyId;
      try {
        const isSaved = this.savedHunts.includes(huntId);
        const response = await fetch(process.env.VUE_APP_API_BASE_URL + `/api/saved-hunts/hunt/${huntId}`, {
          method: isSaved ? 'DELETE' : 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          if (isSaved) {
            this.$store.commit('removeSavedHunt', huntId);
          } else {
            this.$store.commit('addSavedHunt', huntId);
          }
        } else {
          console.error('Failed to toggle saved hunt:', response.status, response.statusText);
          const text = await response.text();
          console.error('Response body:', text);
          this.$emit('error', 'Failed to save hunt');
        }
      } catch (error) {
        console.error('Error toggling saved hunt:', error);
        this.$emit('error', 'Failed to save hunt');
      }
    },
    
    toggleExpanded(event) {
      // Check if there's any selected text
      const selectedText = window.getSelection().toString();
      if (selectedText) {
        return; // Don't toggle if text is selected
      }

      this.isExpanded = !this.isExpanded;
    }
  },
};
</script>

<style scoped>
.hunt-bio-card {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 5px;
  position: relative;
  transition: all 0.2s ease;
}

.hunt-bio-card:hover {
  background-color: #f0f0f0;
}

.hunt-bio-card:active {
  background-color: #e0e0e0;
}

.even-card {
  background-color: #f9f9f9;
}

.odd-card {
  background-color: #fff;
}

.hunt-bio-card p {
  padding: 0;
  margin-bottom: 5px;
  font-size: 0.9rem;
}

/* Add right padding to h5 to prevent overlap with icons */
.hunt-bio-card h5 {
  padding-right: 60px; /* Make room for both icons */
  margin: 0 0 5px 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  font-size: 1rem;
}

.save-icon {
  position: absolute;
  top: 8px;
  right: 36px;
  cursor: pointer;
  font-size: 18px;
  color: #007bff;
  z-index: 1;
}

.save-icon:hover {
  color: #0056b3;
}

.toggle-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
  font-size: 18px;
  color: #007bff;
}

.toggle-icon:hover {
  color: #0056b3;
}

.extended-info {
  margin-top: 15px;
  border-top: 1px solid #eee;
  padding-top: 15px;
}

.section {
  margin-bottom: 20px;
}

.section h6 {
  color: #2c3e50;
  margin-bottom: 8px;
  font-weight: 600;
}

.hunt-details {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  margin-top: 15px;
}

.source-header {
  text-align: right;
  margin-bottom: 15px;
  font-size: 0.9em;
  color: #6c757d;
}

.source-header a {
  color: #6c757d;
  text-decoration: none;
}

.source-header a:hover {
  color: #007bff;
  text-decoration: underline;
}

/* Simple fade transition classes */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
